import { AddOutline, SubtractOutline } from '@wfp/icons-react';
import { Button } from '@wfp/react';
import { DynamicIcon } from 'acr_ui/sources/components/DynamicIcon';
import { Tooltip } from 'acr_ui/sources/components/Tooltip';
import sortBy from 'lodash/sortBy';
import React, { useCallback, useState } from 'react';

import ConfirmationModal from 'src/components/ConfirmationModal';
import CustomToggle from 'src/components/CustomToggle';
import { ASSET_STATUSES } from './constants';
import {
  IModalFooterProps,
  IReviewItemProps,
  IReviewNavProps,
  IReviewsListProps,
  IReviewStatus,
  IReviewStatusProps,
} from './types';
import { formatDate } from './utils';

interface ReviewConfirmationModalProps {
  isOpen: boolean;
  action: string;
  reviewName: string;
  reviewStatus: IReviewStatus | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const ReviewConfirmationModal: React.FC<ReviewConfirmationModalProps> = ({
  isOpen,
  action,
  reviewName,
  reviewStatus,
  onConfirm,
  onCancel,
}) => {
  const getContent = useCallback(() => {
    if (action === '1' && reviewStatus) {
      return (
        <>
          This action will undo the review of the{' '}
          <span className="modal-text-bold">{reviewName}</span> technical area
          completed by{' '}
          <span className="modal-text-bold">{reviewStatus.user}</span> on{' '}
          <span className="modal-text-bold">
            {formatDate(reviewStatus.date)}
          </span>
          .
          <br />
          Do you want to continue?
        </>
      );
    } else {
      return (
        <>
          This action will mark the review of the{' '}
          <span className="modal-text-bold">{reviewName}</span> technical area
          as completed. <br />
          Do you want to continue?
        </>
      );
    }
  }, [action, reviewName, reviewStatus]);

  if (!isOpen) return null;

  return (
    <ConfirmationModal
      open={isOpen}
      onSubmit={onConfirm}
      onClose={onCancel}
      heading="Confirm action"
      content={<div>{getContent()}</div>}
      primaryButtonText="Confirm"
      secondaryButtonText="Cancel"
      customClassName="review-confirmation-modal"
    />
  );
};

export const ReviewNav: React.FC<IReviewNavProps> = ({
  assetsData,
  activeGroup,
  setActiveGroupHandler,
}) => {
  const groupKeys = assetsData.groups.map(group => group.name);

  return (
    <div className="button-switcher">
      {groupKeys.map(groupName => {
        return (
          <Button
            kind={activeGroup === groupName ? 'primary' : 'secondary'}
            key={groupName}
            onClick={() => setActiveGroupHandler(groupName)}
          >
            {groupName}
          </Button>
        );
      })}
    </div>
  );
};

export const ReviewStatusToggle: React.FC<IReviewStatusProps> = ({
  reviewStack,
  reviewStatus,
  showToggle,
  isFetching = false,
  handleReviewStatusToggle,
  reviewName,
}) => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    action: string;
    reviewName: string;
    reviewStatus: IReviewStatus | null;
  }>({
    isOpen: false,
    action: '0',
    reviewName: '',
    reviewStatus: null,
  });

  if (!showToggle) return null;

  const review = `${reviewStack}`;
  const isEnabled = !!reviewStatus;

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isFetching || event.currentTarget.disabled) {
      return;
    }

    const action = event.currentTarget.getAttribute('data-action') || '0';

    setModalState({
      isOpen: true,
      action,
      reviewName: reviewName || '',
      reviewStatus: reviewStatus ? { ...reviewStatus } : null,
    });
  };

  const handleConfirm = () => {
    const { action: actionToExecute } = modalState;

    setModalState(prev => ({ ...prev, isOpen: false }));

    const syntheticEvent = {
      stopPropagation: () => {},
      currentTarget: {
        disabled: false,
        getAttribute: (attr: string) => {
          if (attr === 'data-review') return review;
          if (attr === 'data-action') return actionToExecute;
          return null;
        },
      },
    } as React.MouseEvent<HTMLButtonElement>;

    handleReviewStatusToggle(syntheticEvent);
  };

  const handleCancel = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const iconKwargs = {
      height: '1.2em',
    },
    buttonKwargs = {
      onClick: isFetching ? undefined : handleButtonClick,
      'data-review': review,
    };

  return (
    <>
      <footer className="review-status-change">
        {!isEnabled && (
          <Tooltip
            title="Mark as reviewed"
            trigger="mouseenter"
            theme="light"
            position="top"
            tooltipStyle={{
              popperOptions: { modifiers: { flip: { enabled: false } } },
            }}
          >
            <button
              {...buttonKwargs}
              data-action={0}
              className="enable-review"
              disabled={isFetching}
            >
              <AddOutline
                fill="#0a6eb4"
                description=""
                {...iconKwargs}
                height="20"
                width="20"
              />
            </button>
          </Tooltip>
        )}
        {isEnabled && (
          <Tooltip
            title="Remove review"
            trigger="mouseenter"
            theme="light"
            position="top"
            tooltipStyle={{
              popperOptions: { modifiers: { flip: { enabled: false } } },
            }}
          >
            <button
              {...buttonKwargs}
              data-action={1}
              className="disable-review"
              disabled={isFetching}
            >
              <SubtractOutline
                description=""
                style={{ color: '#c5192d' }}
                {...iconKwargs}
                height="20"
                width="20"
              />
            </button>
          </Tooltip>
        )}
      </footer>
      <ReviewConfirmationModal
        isOpen={modalState.isOpen}
        action={modalState.action}
        reviewName={modalState.reviewName}
        reviewStatus={modalState.reviewStatus}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export const ReviewStatus: React.FC<IReviewStatusProps> = ({
  reviewStack,
  reviewStatus,
  showToggle,
  isFetching = false,
  handleReviewStatusToggle,
  reviewName,
}) => {
  const reviewStatusToggle = showToggle ? (
    <ReviewStatusToggle
      reviewStack={reviewStack}
      reviewStatus={reviewStatus}
      showToggle={showToggle}
      isFetching={isFetching}
      handleReviewStatusToggle={handleReviewStatusToggle}
      reviewName={reviewName}
    />
  ) : null;

  return (
    (!!reviewStatus || reviewStatusToggle) && (
      <div className="review-status">
        {reviewStatus ? (
          <div className="review-status-text">
            Reviewed by <span className="user">{reviewStatus.user}</span> on{' '}
            <span className="date">{formatDate(reviewStatus.date)}</span>
          </div>
        ) : (
          <div className="review-status-text"></div>
        )}
        {reviewStatusToggle}
      </div>
    )
  );
};

export const ReviewItem: React.FC<IReviewItemProps> = ({
  review,
  reviewId,
  reviewStack,
  reviewStatus,
  showToggle,
  isFetching = false,
  handleReviewStatusToggle,
  isEditMode = false,
  toggled,
  handleIconVisibilityToggle,
}) => {
  if (isEditMode) {
    return (
      <div className="review-item" key={reviewId}>
        <figure>
          <DynamicIcon
            url={review.icon_url}
            width={28}
            height={28}
            customClass={`review-icon-aws ${
              review.icon === 'icon-sdg' ? 'icon-sdg ' : ''
            }`}
            disableAutoFillPaths={['icon-sdg', 'icon-environment'].includes(
              review.icon,
            )}
          />
          <figcaption>{review.name}</figcaption>
        </figure>
        <div className="icon-visibility-control">
          <CustomToggle
            id={reviewId}
            labelB=""
            labelA=""
            toggled={toggled}
            onChange={evt =>
              handleIconVisibilityToggle(evt.target.checked, reviewId)
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`review-item${reviewStatus ? ' checked' : ''}`}
      key={reviewId}
    >
      <figure>
        <DynamicIcon
          url={review.icon_url}
          width={28}
          height={28}
          disableAutoFillPaths={['icon-sdg', 'icon-environment'].includes(
            review.icon,
          )}
          customClass={`review-icon-aws ${
            review.icon === 'icon-sdg' ? 'icon-sdg ' : ''
          } ${reviewStatus ? 'review-status-btn' : ''}`}
        />
        <figcaption>{review.name}</figcaption>
      </figure>
      <ReviewStatus
        reviewStack={reviewStack}
        reviewStatus={reviewStatus}
        showToggle={showToggle}
        isFetching={isFetching}
        handleReviewStatusToggle={handleReviewStatusToggle}
        reviewName={review.name}
      />
    </div>
  );
};

export const ReviewsList: React.FC<IReviewsListProps> = ({
  assetsData,
  activeGroup,
  editReviewsMode,
  toggleIconVisibility,
  handleIconVisibilityToggle,
  props,
  isFetching = false,
  handleReviewStatusToggle,
  searchQuery = '',
}) => {
  const sortedReviews = assetsData?.reviews
    ? sortBy(assetsData.reviews, 'name')
    : [];

  const filteredReviews = sortedReviews.filter(review => {
    if (!searchQuery) return true;
    return review.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const groupKeys = assetsData.groups.map(group => group.name),
    activeGroupIndex = groupKeys.indexOf(activeGroup),
    activeGroupName =
      activeGroupIndex !== -1
        ? assetsData.groups[activeGroupIndex].name
        : groupKeys.length > 0
          ? assetsData.groups[0].name
          : null;

  const listClasses = ['reviews-list'];

  if (!activeGroupName) return null;

  if (editReviewsMode) {
    listClasses.push('being-edited');
    const selectedGroup = [],
      deselectedGroup = [];
    const assetsDataArray = sortBy(Object.entries(assetsData.reviews), [
      function (o) {
        return o[1].name;
      },
    ]);

    const filteredAssetsDataArray = assetsDataArray.filter(([, review]) => {
      if (!searchQuery) return true;
      return review.name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const idsInOrder = filteredAssetsDataArray.map(item => {
      return item[0];
    });

    for (const reviewId in idsInOrder) {
      const review = assetsData.reviews[idsInOrder[reviewId]],
        initiallySelected =
          typeof review.groups[activeGroupName] !== 'undefined',
        currentContainer = initiallySelected ? selectedGroup : deselectedGroup;

      const toggled = (toggleIconVisibility[activeGroup] || {}).hasOwnProperty(
        idsInOrder[reviewId],
      )
        ? toggleIconVisibility[activeGroup][idsInOrder[reviewId]]
        : typeof review.groups[activeGroupName] !== 'undefined';

      currentContainer.push(
        <ReviewItem
          key={idsInOrder[reviewId]}
          review={review}
          reviewId={idsInOrder[reviewId]}
          isEditMode={true}
          toggled={toggled}
          handleIconVisibilityToggle={handleIconVisibilityToggle}
        />,
      );
    }

    return (
      <>
        {selectedGroup.length > 0 && (
          <>
            <h5 className="group-title">Selected functions</h5>
            <div className={['selected', ...listClasses].join(' ')}>
              {selectedGroup}
            </div>
          </>
        )}
        {deselectedGroup.length > 0 && (
          <>
            <h5 className="group-title">Available functions</h5>
            <div className={['deselected', ...listClasses].join(' ')}>
              {deselectedGroup}
            </div>
          </>
        )}
        {selectedGroup.length === 0 && deselectedGroup.length === 0 && (
          <div className="no-results">
            {searchQuery ? 'No results' : 'No functions available'}
          </div>
        )}
      </>
    );
  } else {
    const activeGroupComponents = [];
    const reviewStatusesData = props[ASSET_STATUSES].data
        ? props[ASSET_STATUSES].data.actions
        : null,
      showToggle =
        !!reviewStatusesData &&
        !!props.hasPermissionToMark &&
        props.sectionData?.data?.permissions?.can_change_reviewaction;

    for (const reviewId in filteredReviews) {
      const review = filteredReviews[reviewId],
        reviewStack = review.groups[activeGroupName];

      if (typeof reviewStack === 'undefined') continue;

      const reviewStatus = reviewStatusesData
        ? reviewStatusesData[reviewStack]
        : null;

      activeGroupComponents.push(
        <ReviewItem
          key={reviewId}
          review={review}
          reviewId={reviewId}
          reviewStack={reviewStack}
          reviewStatus={reviewStatus}
          showToggle={showToggle}
          isFetching={isFetching}
          handleReviewStatusToggle={handleReviewStatusToggle}
          isEditMode={false}
        />,
      );
    }

    if (activeGroupComponents.length === 0) {
      return (
        <div className={listClasses.join(' ')}>
          <div className="no-results">
            {searchQuery ? 'No results' : 'No functions available'}
          </div>
        </div>
      );
    }

    return <div className={listClasses.join(' ')}>{activeGroupComponents}</div>;
  }
};

export const ModalFooter: React.FC<IModalFooterProps> = ({
  editReviewsMode,
  handleSubmitEditedIcons,
  handleCancelEditIcons,
  handleIconVisibilityToggleAll,
  handleStartEditIcons,
}) => {
  return editReviewsMode ? (
    <>
      <Button
        kind="secondary"
        onClick={() => handleIconVisibilityToggleAll(false)}
        data-test-id="deselect-all-button"
        className="deselect-btn"
      >
        Deselect all
      </Button>
      <Button
        kind="secondary"
        onClick={() => handleIconVisibilityToggleAll(true)}
        data-test-id="select-all-button"
      >
        Select all
      </Button>
      <Button
        className="float-right"
        onClick={handleSubmitEditedIcons}
        data-test-id="save-button"
      >
        Save
      </Button>
      <Button
        className="float-right"
        onClick={handleCancelEditIcons}
        data-test-id="cancel-button"
      >
        Cancel
      </Button>
    </>
  ) : (
    <Button
      className="float-right"
      onClick={handleStartEditIcons}
      data-test-id="add-remove-icons-button"
    >
      Add/Remove functions
    </Button>
  );
};
